<template>
  <div>
    <h3 class="mt-3 mb-4 title primary--text">
      {{ $t('Equipment Management') }}
    </h3>
    <v-row class="white">
      <v-col>
        <div class="d-flex align-center">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('Search')"
            single-line
            hide-details
          />
          <v-btn
            class="ml-4"
            color="secondary"
            @click="exportTable"
          >
            {{ $t('export') }}
          </v-btn>
        </div>

        <v-data-table
          :headers="headers"
          :items="equipmentFiltered"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :group-by.sync="groupBy"
        >
          <template v-slot:header.laboratory.name="{header}">
            {{ header.text }}
            <span
              class="mx-3"
              @click="groupBy = header.value"
            >
              {{ $t('Group') }}
            </span>
          </template>

          <template v-slot:body.prepend="data">
            <tr>
              <template v-for="header in data.headers">
                <td :key="header.value">
                  <template v-if="header.value === 'type'">
                    <v-autocomplete
                      v-model="filterCategory"
                      class="my-2"
                      :items="equipmentCategories"
                      dense
                      hide-details
                      outlined
                      color="blue-grey lighten-2"
                      :label="$t('Filter by Type')"
                      cache-items
                      clearable
                      return-object
                      item-text="category"
                    />
                    <v-autocomplete
                      v-if="filterCategory"
                      v-model="filterSubCategory"
                      class="my-2"
                      :items="equipmentSubCategories"
                      dense
                      hide-details
                      outlined
                      color="blue-grey lighten-2"
                      :label="$t('Filter by subtype')"
                      cache-items
                      clearable
                    />
                  </template>

                  <v-autocomplete
                    v-else-if="header.value === 'condition'"
                    v-model="filterCondition"
                    class="my-2"
                    :items="equipmentConditions"
                    dense
                    hide-details
                    outlined
                    color="blue-grey lighten-2"
                    :label="$t('Filter By condition')"
                    cache-items
                    clearable
                  />

                  <v-autocomplete
                    v-else-if="header.value === 'name'"
                    v-model="filterName"
                    class="my-2"
                    :items="equipmentNames"
                    dense
                    hide-details
                    outlined
                    color="blue-grey lighten-2"
                    :label="$t('Filter By Name')"
                    cache-items
                    clearable
                  />
                </td>
              </template>
            </tr>
          </template>
          <template v-slot:body.append="data">
            <tr>
              <template v-for="header in data.headers">
                <td :key="header.value">
                  <template v-if="header.value === 'condition'">
                    <b>Total / Functional :</b> <br>
                    <b>
                      {{ equipmentFilteredTotalFunctioning.total }} /
                      {{ equipmentFilteredTotalFunctioning.functioning }}
                    </b>
                  </template>
                </td>
              </template>
            </tr>
          </template>

          <template v-slot:group.header="{group, groupBy, headers, isOpen, toggle, remove, items}">
            <td
              :colspan="headers.length"
              class="text-start"
            >
              <div class="d-flex">
                <div>
                  <v-btn
                    icon
                    small
                    @click="toggle"
                  >
                    <v-icon v-if="isOpen">
                      mdi-minus
                    </v-icon>
                    <v-icon v-else>
                      mdi-plus
                    </v-icon>
                  </v-btn>

                  {{ groupByNameMapper[groupBy] }}: {{ group }}
                  <v-btn
                    icon
                    small
                    @click="remove"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
                <div
                  v-if="groupBy == 'laboratory.name'"
                  class="ml-auto"
                >
                  <v-btn
                    icon
                    :to="{name: 'SupervisionList', params: {laboratoryId: items[0].laboratory.id}}"
                    target="_blank"
                  >
                    <v-icon
                      small
                    >
                      mdi-open-in-new
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </td>
          </template>

          <template v-slot:item.type="{ item }">
            <div class="d-flex flex-column">
              <div>
                {{ item.type.subCategory }}
              </div>
              <div class="text--disabled subtitle-2">
                {{ item.type.category }}
              </div>
            </div>
          </template>

          <template v-slot:item.state="{ item }">
            <div class="d-flex flex-wrap align-center">
              <v-sheet
                v-if="item.calculatedMaintenance"
                :class="item.calculatedMaintenance.color"
                class="rounded-circle d-inline-block mr-3"
                elevation="8"
                height="20"
                width="20"
              />
              {{ item.lastMaintenance.format('MM/DD/YYYY') }}
            </div>
          </template>

          <!--          <template v-slot:item.maintenanceCalibrationDates="{ item }">-->
          <!--            <div class="d-flex flex-wrap">-->
          <!--              <v-sheet-->
          <!--                v-if="item.calculatedMaintenance"-->
          <!--                :class="item.calculatedMaintenance.color"-->
          <!--                class="rounded-circle d-inline-block ma-1"-->
          <!--                elevation="8"-->
          <!--                height="20"-->
          <!--                width="20"-->
          <!--              />-->
          <!--              <v-chip-->
          <!--                v-for="(date, index) in (item.maintenanceCalibrationDates)"-->
          <!--                :key="date"-->
          <!--                class="mr-1 my-1"-->
          <!--                small-->
          <!--              >-->
          <!--                {{ date }}-->
          <!--              </v-chip>-->
          <!--            </div>-->
          <!--          </template>-->

          <!--          <template v-slot:item.files="{ item }">-->
          <!--            <files-->
          <!--              :files="item.files"-->
          <!--              :file-link-path="getEquipmentFileLinkPath(item)"-->
          <!--              :canRemove="false"-->
          <!--            />-->
          <!--          </template>-->

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              :to="{name: 'LaboratoryEquipmentInventory', params: {laboratoryId: item.laboratory.id}}"
              target="_blank"
            >
              <v-icon
                small
              >
                mdi-open-in-new
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>

import moment from 'moment'
import {
  export_json_to_excel_custom
} from '@/vendor/Export2Excel'
// import Files from "@/components/common/Files";

export default {
  name: "ReqToolEquipment",

  components: {
    // Files,
  },

  props: {
    laboratories: {
      type: Array,
      required: true,
    },
    country: {
      type: Object,
      required: true,
    }
  },

  data: () => {
    return {
      search: '',
      laboratoriesCopy: [],
      sortDesc: false,
      sortBy: 'laboratory',
      groupByNameMapper: {
        'laboratory.name': 'Laboratory Name',
        'type': 'Equipment Type'
      },
      groupBy: [],

      filterCategory: '',
      filterSubCategory: '',
      filterCondition: '',
      filterName: '',
    }
  },

  computed: {
    headers() {
      let headers = [
        {
          text: this.$t('Laboratory'),
          value: 'laboratory.name',
          align: 'start',
          sortable: true,
          groupable: true,
        },
        {
          text: this.$t('Id'),
          value: 'eqId',
          align: 'start',
          sortable: true,
          groupable: false,
        },
        {
          text: this.$t('Type'),
          value: 'type',
          align: 'start',
          sortable: true,
          groupable: false,
          width: 170
        },
        {
          text: this.$t('Name'),
          value: 'name',
          align: 'start',
          sortable: true,
          groupable: false,
          width: 170
        },
        {
          text: this.$t('Brand'),
          value: 'brand',
          align: 'start',
          sortable: true,
          groupable: false,
        },
        {
          text: this.$t('Serial Number'),
          value: 'serialNumber',
          align: 'start',
          sortable: true,
          groupable: false,
        },
        // {
        //     text: 'Date Purchased',
        //     value: 'datePurchased',
        //     align: 'start',
        //     sortable: true,
        //     groupable: false,
        // },
        // {
        //     text: 'Year Manufacturing',
        //     value: 'yearManufacturing',
        //     align: 'start',
        //     sortable: true,
        //     groupable: false,
        // },
        // {
        //     text: 'Room',
        //     value: 'room',
        //     align: 'start',
        //     sortable: true,
        //     groupable: false,
        // },
        {
          text: this.$t('Condition'),
          value: 'condition',
          align: 'start',
          sortable: true,
          groupable: false,
          width: 170
        },
        {
          text: this.$t('State'),
          value: 'state',
          align: 'start',
          sortable: false,
          filterable: false,
          groupable: false,
        },
        // {
        //     text: 'Maintenance-Calibration Dates',
        //     value: 'maintenanceCalibrationDates',
        //     align: 'start',
        //     width: '400',
        //     sortable: true,
        //     groupable: false,
        // },
        {
          text: this.$t('Actions'),
          value: 'actions',
          align: 'end',
          sortable: false,
          filterable: false,
          groupable: false,
        },

      ];

      headers.forEach(e => {
        e.class = 'text-no-wrap'
      });
      return headers
    },

    equipmentItems() {
      return this.laboratoriesCopy.map(lab => lab.equipment).flat();
    },

    equipmentFiltered() {
      let equipmentItems = this.equipmentItems;
      if (this.filterSubCategory) {
        equipmentItems = this.equipmentItems.filter(e => (
            e.type.category === this.filterCategory.category &&
            e.type.subCategory === this.filterSubCategory
        ))
      } else if (this.filterCategory?.category) {
        equipmentItems = this.equipmentItems.filter(e => e.type.category === this.filterCategory.category)
      }

      if (this.filterCondition) {
        equipmentItems = this.equipmentItems.filter(e => e.condition === this.filterCondition)
      }

      if (this.filterName) {
        equipmentItems = this.equipmentItems.filter(e => e.name === this.filterName)
      }

      return equipmentItems
    },

    equipmentFilteredTotalFunctioning() {
      let functioning = this.equipmentFiltered.reduce((a, r) => {
        if (r.condition === 'Functioning') {
          a += 1
        }
        return a;
      }, 0);
      return {
        functioning,
        total: this.equipmentFiltered.length
      }
    },

    equipmentCategories() {
      let data = this.equipmentItems.reduce((a, r) => {
        if (!a[r.type.category]) {
          a[r.type.category] = {category: r.type.category, subCategories: {}};
        }
        a[r.type.category].subCategories[r.type.subCategory] = {};
        return a;
      }, {});
      data = Object.values(data).map(e => {
        e.subCategories = Object.keys(e.subCategories);
        return e
      });
      return data
    },

    equipmentSubCategories() {
      return this.filterCategory?.subCategories;
    },

    equipmentConditions() {
      let data = this.equipmentItems.reduce((a, r) => {
        if (r.condition) a[r.condition] = {};
        return a;
      }, {});

      return Object.keys(data)
    },

    equipmentNames() {
      let data = this.equipmentItems.reduce((a, r) => {
        a[r.name] = {};
        return a;
      }, {});

      return Object.keys(data)
    },
  },

  mounted() {
    this.laboratoriesCopy = JSON.parse(JSON.stringify(this.laboratories));
    this.laboratoriesCopy.forEach(lab => lab.equipment.forEach(eq => {
      eq.laboratory = lab;
      eq.lastMaintenance = moment.max(eq.maintenanceCalibrationDates.map(e => moment(e)));
      eq.calculatedMaintenance = this.calculateValidMaintenance(eq)
    }));
  },

  methods: {
    getEquipmentFileLinkPath(item) {
      return `/laboratory/${this.laboratoryId}/equipment/${item.id}/files`
    },

    calculateValidMaintenance(eq) {
      let inventoryData = this.country.equipment.find(e =>
          e.category === eq.type.category &&
          e.subCategory === eq.type.subCategory
      );

      if (inventoryData?.maintenanceCalibration === '0') {
        return {
          color: 'gray',
          name: 'good',
          value: true
        }
      }

      if (!Array.isArray(eq.maintenanceCalibrationDates)) {
        return {
          color: 'error',
          name: 'bad',
          value: false
        }
      }

      let itemLastMaintenance = eq.lastMaintenance;

      let today = moment();

      let maintenanceTime = moment(itemLastMaintenance).add(inventoryData.maintenanceCalibration, 'weeks');

      let maintenanceDiff = maintenanceTime.diff(today, 'week');

      if (maintenanceDiff <= 2 || itemLastMaintenance === undefined) {
        return {
          color: 'error',
          name: 'bad',
          value: false
        }
      } else if (Math.round(maintenanceDiff * 100 / inventoryData.maintenanceCalibration) < 40) {
        return {
          color: 'warning',
          name: 'average',
          value: true
        }
      } else {
        return {
          color: 'success',
          name: 'good',
          value: true
        }
      }
    },

    exportTable() {
      let multiData = [];

      {
        const tHeader = [
          '#', 'Laboratory', 'eqId', 'Name', 'Type', 'Brand',
          'Serial Number', 'Date Purchased', 'Year Manufacturing',
          'Room', 'Condition', 'State', 'Maintenance-Calibration Dates',
        ];


        const filterVal = [
          '#', 'laboratory', 'id', 'name', 'type', 'brand',
          'serialNumber', 'datePurchased', 'yearManufacturing',
          'room', 'condition', 'state', 'maintenanceCalibrationDates',
        ];

        const deepValues = {
          laboratory(item) {
            return item?.laboratory?.name || ''
          },
          type(item) {
            return `${item.type.category} / ${item.type.subCategory}`
          },
          maintenanceCalibrationDates(item) {
            return item.maintenanceCalibrationDates.length ? item.maintenanceCalibrationDates.join(', ') : ''
          },
          state(item) {
            return item.calculatedMaintenance.name
          }
          // createdAt(item) {
          //     return moment(item.createdAt).format('YYYY/MM/DD')
          // }
        };


        let data = this.equipmentFiltered.map((v, index) => {
          return filterVal.map(j => {
            if (j === '#') return index + 1;
            // if (v[j]) {
            if (j in deepValues) {
              return deepValues[j](v)
            } else {
              return v[j].toString()
            }
            // }

            // return ''
          });
        }).filter(e => e);

        multiData.push({name: 'Equipment', header: tHeader, data})
      }

      export_json_to_excel_custom({
        multiData,
        filename: 'equipment',
        autoWidth: true,
      })

    }
  },

}
</script>

<style scoped>

</style>
